import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { mobileUiOchags, mobileSelectedFocusData } from '../../../utils/mobileCalculations';
import DataSection from './DataSection';
import CalculationsSection from './CalculationsSection';
import RecommendedMeasuresSection from './RecommendedMeasuresSection';
import OchagSection from './OchagSection';
import OchagCardsSection from './OchagCardsSection';
import OchagCardsSectionForm5 from '../Form5/OchagCardsSection';
import { getEmptyFormData } from './constants';
import { resolveHasRecommendedMeasures, RECOMMENDED_MEASURES_NOTICE } from '../../../utils/recommendedMeasures';
import { usePloshadVydel, useZapasCalculations } from '../Form4/calculationHooks';
import { useTotalsCalculations } from '../Form4/calculationHooks';
import { calculateDamagePercentage } from '../Form5/utils';
import { useTotalsByNasazhdeniya as useTotalsByNasazhdeniyaForm5 } from '../Form5/calculationHooks';
import { usePloshadVydel as usePloshadVydelForm5, useTotalDensitySum as useTotalDensitySumForm5, useYarusTotals as useYarusTotalsForm5 } from '../Form5/calculationHooks';
import { calculateDamage_0 } from '../Form5/utils';

const Form6 = ({ handbooks, formData, setFormData, selectedDoc, form4Data, form5Data, form2Data, independentForm2Data, dataType, form1Data, isActive, selectedForm45 = 4, isNewCard = false }) => {
  

  // Состояние для слайдера таблицы
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  const tableScrollRef = useRef(null);

  // Состояние для слайдера таблицы расчетов
  const [calculationsScrollPosition, setCalculationsScrollPosition] = useState(0);
  const calculationsScrollRef = useRef(null);
  
  // Состояние для принудительного обновления displayData
  const [dataUpdateKey, setDataUpdateKey] = useState(0);
  
  // Ref для отслеживания предыдущей длины данных Form2 (как в Form4)
  const prevForm2LengthRef = useRef(0);

  // Функция для обновления данных формы
  // ВАЖНО: не трогаем selectedOchags и ochagData - они хранятся на верхнем уровне formData
  const updateFormData = useCallback((newData) => {
    setFormData(prev => ({
      ...prev,
      form6: newData
      // selectedOchags и ochagData остаются на верхнем уровне formData, не трогаем их
    }));
  }, [setFormData]);

  // Функция для обновления общих полей (расчётная остаточная полнота, класс устойчивости)
  const updateCommonField = useCallback((field, value) => {
    const currentFormData = formData || getEmptyFormData();
    const currentForm6Data = currentFormData.form6 || getEmptyFormData();
    const newForm6Data = {
      ...currentForm6Data,
      [field]: value
    };
    updateFormData(newForm6Data);
  }, [formData, updateFormData]);

  // Получаем рекомендуемые мероприятия из formData
  const recommendedMeasures = useMemo(() => {
    
    const currentForm6Data = formData?.form6 || getEmptyFormData();
    
    const measures = currentForm6Data.recommended_measures || getEmptyFormData().recommended_measures;
    
    return measures;
  }, [formData]);

  // Наличие рекомендованных мероприятий: null — не выбрано, true — да, false — нет.
  // Хранится в карточке как form6.activities_required (YES / NO / NONE); правило для
  // значения, которого ещё нет, — в utils/recommendedMeasures.
  const hasRecommendedMeasures = useMemo(
    () => resolveHasRecommendedMeasures({
      form6State: formData?.form6, isNewCard
    }),
    [formData, isNewCard]
  );

  // Функция для добавления новой строки в рекомендуемые мероприятия
  const handleAddRecommendedMeasure = useCallback((afterId) => {
    const currentForm6Data = formData?.form6 || getEmptyFormData();
    const currentMeasures = currentForm6Data.recommended_measures || getEmptyFormData().recommended_measures;
    
    // Находим максимальный ID
    const maxId = currentMeasures.length > 0 
      ? Math.max(...currentMeasures.map(item => item.id))
      : 0;
    
    const newMeasure = {
      id: maxId + 1,
      rekvidmer: '',
      srokmzl: '',
      srekme: '',
      primechmzl: ''
    };
    
    if (afterId) {
      // Вставляем после указанной строки
      const afterIndex = currentMeasures.findIndex(item => item.id === afterId);
      const newMeasures = [...currentMeasures];
      newMeasures.splice(afterIndex + 1, 0, newMeasure);
      
      updateFormData({
        ...currentForm6Data,
        recommended_measures: newMeasures
      });
    } else {
      // Добавляем в конец
      updateFormData({
        ...currentForm6Data,
        recommended_measures: [...currentMeasures, newMeasure]
      });
    }
  }, [formData, updateFormData]);

  // Функция для удаления строки из рекомендуемых мероприятий
  const handleDeleteRecommendedMeasure = useCallback((id) => {
    const currentForm6Data = formData?.form6 || getEmptyFormData();
    const currentMeasures = currentForm6Data.recommended_measures || getEmptyFormData().recommended_measures;
    
    if (currentMeasures.length <= 1) {
      return; // Не удаляем последнюю строку
    }
    
    const newMeasures = currentMeasures.filter(item => item.id !== id);
    
    updateFormData({
      ...currentForm6Data,
      recommended_measures: newMeasures
    });
  }, [formData, updateFormData]);

  // Функция для обновления поля в рекомендуемых мероприятиях
  const handleUpdateRecommendedMeasureField = useCallback((id, field, value) => {
    
    // Преобразуем значение в строку для полей справочников (например, rekvidmer)
    const normalizedValue = (field === 'rekvidmer' && value !== null && value !== undefined) 
      ? String(value) 
      : value;
    
    // Используем функциональное обновление для получения актуального состояния
    setFormData(prev => {
      
      const currentForm6Data = prev?.form6 || getEmptyFormData();
      
      const currentMeasures = currentForm6Data.recommended_measures || getEmptyFormData().recommended_measures;
      
      const newMeasures = currentMeasures.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: normalizedValue };
          return updated;
        }
        return item;
      });
      
      const newState = {
        ...prev,
        form6: {
          ...currentForm6Data,
          recommended_measures: newMeasures
        }
      };
      
      return newState;
    });
  }, [setFormData]);

  // Отслеживание изменений в independentForm2Data для синхронизации (как в Form4)
  useEffect(() => {
    if (independentForm2Data && independentForm2Data.poroda_characteristics_dependent) {
      const form2Length = independentForm2Data.poroda_characteristics_dependent.length;
      const lengthChanged = form2Length !== prevForm2LengthRef.current;
      
      // Обновляем ref с текущей длиной
      prevForm2LengthRef.current = form2Length;
      
      // Принудительно обновляем displayData при изменении длины данных Form2
      if (lengthChanged) {
        setDataUpdateKey(prev => prev + 1);
      }
    }
  }, [independentForm2Data]);

  // Получаем данные для отображения - берем из form4Data, из Form2 или из formData.form6
  const displayData = useMemo(() => {
    
    // Проверяем, есть ли реально заполненные данные из Form4 (не пустые строки)
    // Form4 использует dolap, kodporoda вместо dolya, poroda
    const hasForm4Data = form4Data && form4Data.data && form4Data.data.length > 0 && 
      form4Data.data.some(item => item.yarus || item.kodporoda || item.dolap);
    
    // Получаем сохраненные общие поля из formData.form6
    const currentForm6Data = formData?.form6 || {};
    const savedCommonFields = {
      raschetnaya_ostatochnaya_polnota: currentForm6Data.raschetnaya_ostatochnaya_polnota || '',
      klass_ustoychivosti_nasazhdeniya: currentForm6Data.klass_ustoychivosti_nasazhdeniya || ''
    };
    
    // Если выбрана форма №4 и есть данные из Form4, используем их
    if (selectedForm45 === 4 && hasForm4Data) {
      // Преобразуем данные Form4 в формат Form6
      // Form4 использует: dolap, kodporoda, bpodolap, osldolap, sosldolap, usdolap, 
      // svsuhdolap, svvtrdolap, svburdolap, stsuhdolap, stvtrdolap, stburdolap
      // Form6 использует: dolya, poroda, i_zdor, ii_oslab, iii_sosl, iv_usyh,
      // va_svss, vb_svvv, vc_svbl, vg_stss, vd_stvv, ve_stbl
      return {
        data: form4Data.data.map(item => ({
          id: item.id,
          yarus: item.yarus || '',
          dolya: item.dolap || '',  // Form4 использует dolap
          poroda: item.kodporoda || '',  // Form4 использует kodporoda
          // Преобразуем поля санитарного состояния из Form4 в Form6
          i_zdor: item.bpodolap !== undefined ? item.bpodolap : 100,
          ii_oslab: item.osldolap !== undefined ? item.osldolap : 0,
          iii_sosl: item.sosldolap !== undefined ? item.sosldolap : 0,
          iv_usyh: item.usdolap !== undefined ? item.usdolap : 0,
          va_svss: item.svsuhdolap !== undefined ? item.svsuhdolap : 0,
          vb_svvv: item.svvtrdolap !== undefined ? item.svvtrdolap : 0,
          vc_svbl: item.svburdolap !== undefined ? item.svburdolap : 0,
          vg_stss: item.stsuhdolap !== undefined ? item.stsuhdolap : 0,
          vd_stvv: item.stvtrdolap !== undefined ? item.stvtrdolap : 0,
          ve_stbl: item.stburdolap !== undefined ? item.stburdolap : 0,
          // Новые поля для доли повреждённых деревьев
          dolya_povrezhdennyh_po_prichinam: item.dolya_povrezhdennyh_po_prichinam || '',
          dolya_povrezhdennyh_dlya_ochaga: item.dolya_povrezhdennyh_dlya_ochaga || ''
        })),
        ...savedCommonFields
      };
    }

    // Если выбрана форма №5 и есть данные из Form5, используем их как источник строк
    if (selectedForm45 === 5 && form5Data && form5Data.data && form5Data.data.length > 0) {
      return {
        data: form5Data.data.map(item => ({
          id: item.id,
          yarus: item.yarus || '',
          dolya: item.dolap || '',
          poroda: item.kodporoda || '',
          // В режиме Form5 эти поля в Form6-таблице не используются, оставляем по умолчанию
          i_zdor: 100,
          ii_oslab: 0,
          iii_sosl: 0,
          iv_usyh: 0,
          va_svss: 0,
          vb_svvv: 0,
          vc_svbl: 0,
          vg_stss: 0,
          vd_stvv: 0,
          ve_stbl: 0
        })),
        ...savedCommonFields
      };
    }
    
    // Если данных из Form4 нет, но есть данные из Form2, создаем данные на основе Form2
    // Сначала проверяем independentForm2Data
    let form2SourceData = null;
    if (independentForm2Data && 
        independentForm2Data.poroda_characteristics_dependent && 
        Array.isArray(independentForm2Data.poroda_characteristics_dependent) &&
        independentForm2Data.poroda_characteristics_dependent.length > 0 &&
        independentForm2Data.poroda_characteristics_dependent.some(item => item.yarus || item.kodporodato || item.dolap)) {
      form2SourceData = independentForm2Data.poroda_characteristics_dependent;
    }
    // Если в independentForm2Data нет данных, проверяем form2Data
    else if (form2Data && form2Data[dataType] && 
             form2Data[dataType].poroda_characteristics_dependent &&
             Array.isArray(form2Data[dataType].poroda_characteristics_dependent) &&
             form2Data[dataType].poroda_characteristics_dependent.length > 0 &&
             form2Data[dataType].poroda_characteristics_dependent.some(item => item.yarus || item.kodporodato || item.dolap)) {
      form2SourceData = form2Data[dataType].poroda_characteristics_dependent;
    }
    
    if (form2SourceData) {
      return {
        data: form2SourceData.map((item, index) => ({
          id: index + 1,
          yarus: item.yarus || '',
          // Берём долю/породу из новых полей dolap/kodporodato,
          // а внутренняя модель Form6 по-прежнему использует dolya/poroda
          dolya: item.dolap || '',
          poroda: item.kodporodato || '',
          // Используем значения по умолчанию для санитарного состояния
          i_zdor: 100,
          ii_oslab: 0,
          iii_sosl: 0,
          iv_usyh: 0,
          va_svss: 0,
          vb_svvv: 0,
          vc_svbl: 0,
          vg_stss: 0,
          vd_stvv: 0,
          ve_stbl: 0
        })),
        ...savedCommonFields
      };
    }
    
    // Иначе используем сохраненные данные Form6 или пустые данные
    if (formData && formData.form6 && formData.form6.data && formData.form6.data.length > 0) {
      return formData.form6;
    }
    
    return getEmptyFormData();
  }, [form4Data, form5Data, selectedForm45, independentForm2Data, form2Data, dataType, formData, dataUpdateKey]);

  // Используем хуки для расчетов (как в Form4)
  const { calculatePloshadVydel } = usePloshadVydel(form1Data, dataType);
  
  const {
    calculateTotalDensitySum
  } = useZapasCalculations(form1Data, independentForm2Data, calculatePloshadVydel);
  
  // Для расчетов используем form4Data, если он есть, иначе преобразуем displayData в формат Form4
  const dataForCalculations = useMemo(() => {
    // Проверяем, есть ли данные Form4 с правильными полями (dolap, kodporoda)
    if (form4Data && form4Data.data && form4Data.data.length > 0 && 
        form4Data.data.some(item => item.yarus || item.kodporoda || item.dolap)) {
      return form4Data;
    }
    
    // Если данных Form4 нет, преобразуем displayData в формат Form4 для расчетов
    // displayData использует поля Form6 (dolya, poroda, i_zdor и т.д.)
    // Нужно преобразовать в поля Form4 (dolap, kodporoda, bpodolap и т.д.)
    if (displayData && displayData.data && displayData.data.length > 0) {
      return {
        ...displayData,
        data: displayData.data.map(item => ({
          id: item.id,
          yarus: item.yarus || '',
          dolap: item.dolya || '',  // Преобразуем dolya -> dolap
          kodporoda: item.poroda || '',  // Преобразуем poroda -> kodporoda
          // Преобразуем поля санитарного состояния из Form6 в Form4
          bpodolap: item.i_zdor !== undefined ? item.i_zdor : 100,
          osldolap: item.ii_oslab !== undefined ? item.ii_oslab : 0,
          sosldolap: item.iii_sosl !== undefined ? item.iii_sosl : 0,
          usdolap: item.iv_usyh !== undefined ? item.iv_usyh : 0,
          svsuhdolap: item.va_svss !== undefined ? item.va_svss : 0,
          svvtrdolap: item.vb_svvv !== undefined ? item.vb_svvv : 0,
          svburdolap: item.vc_svbl !== undefined ? item.vc_svbl : 0,
          stsuhdolap: item.vg_stss !== undefined ? item.vg_stss : 0,
          stvtrdolap: item.vd_stvv !== undefined ? item.vd_stvv : 0,
          stburdolap: item.ve_stbl !== undefined ? item.ve_stbl : 0,
          // Для СКС и отпада используем вычисляемые значения или пустые строки
          sksporoda: '',
          ooporoda: '',
          toporoda: ''
        }))
      };
    }
    
    return displayData;
  }, [form4Data, displayData]);

  const {
    calculateTotalsByNasazhdeniya: calculateTotalsByNasazhdeniyaRaw
  } = useTotalsCalculations(
    dataForCalculations,
    independentForm2Data,
    form1Data,
    calculatePloshadVydel,
    calculateTotalDensitySum
  );

  // Преобразуем результат из формата Form4 (sksporoda, ooporoda, toporoda) в формат Form6 (sks, otpad_obsh, otpad_tek)
  const calculateTotalsByNasazhdeniya = useCallback(() => {
    const rawTotals = calculateTotalsByNasazhdeniyaRaw ? calculateTotalsByNasazhdeniyaRaw() : {};
    return {
      sks: rawTotals.sksporoda || '0.00',
      sksRaw: rawTotals.sksporodaRaw,
      otpad_obsh: rawTotals.ooporoda || '0.00',
      otpad_obshRaw: rawTotals.ooporodaRaw,
      otpad_tek: rawTotals.toporoda || '0.00'
    };
  }, [calculateTotalsByNasazhdeniyaRaw]);

  // Функции для расчета в форме №5 (для OchagCardsSection)
  const calculateDamage_0_func_Form5 = useCallback((item) => calculateDamage_0(item), []);
  const calculateDamagePercentage_func_Form5 = useCallback((item) => calculateDamagePercentage(item), []);
  
  // Хуки для расчетов формы №5
  const { calculatePloshadVydel: calculatePloshadVydelForm5 } = usePloshadVydelForm5(form1Data, dataType);
  const { calculateTotalDensitySum: calculateTotalDensitySumForm5 } = useTotalDensitySumForm5(form1Data, independentForm2Data, calculatePloshadVydelForm5);
  const { calculateYarusTotals: calculateYarusTotalsForm5 } = useYarusTotalsForm5(form5Data, calculateDamage_0_func_Form5, calculateDamagePercentage_func_Form5);
  const { calculateTotalsByNasazhdeniya: calculateTotalsByNasazhdeniyaForm5 } = useTotalsByNasazhdeniyaForm5(
    form5Data,
    independentForm2Data,
    form1Data,
    calculatePloshadVydelForm5,
    calculateTotalDensitySumForm5,
    calculateYarusTotalsForm5,
    calculateDamage_0_func_Form5,
    calculateDamagePercentage_func_Form5
  );

  // Функция для расчета доли поврежденных деревьев для OchagSection
  // Работает с form4Data или form5Data в зависимости от selectedForm45
  const calculateTotalDamagePercentage = useCallback(() => {
    if (selectedForm45 === 5) return calculateTotalsByNasazhdeniyaForm5().dolapovr;
    const totals = calculateTotalsByNasazhdeniyaRaw();
    return (100 - Number(totals.bpodolap || 0)).toFixed(2);
  }, [selectedForm45, calculateTotalsByNasazhdeniyaForm5, calculateTotalsByNasazhdeniyaRaw]);

  // Функция для получения значения доли поврежденных деревьев для конкретной причины по ID (для формы №4)
  const getOchagDamageValueForCause = useCallback((causeId) =>
    mobileUiOchags(4, form4Data, handbooks, independentForm2Data, form1Data, dataType)
      .find(cause => String(cause.id) === String(causeId))?.osd.toFixed(1) ?? '',
    [form4Data, handbooks, independentForm2Data, form1Data, dataType]
  );

  const getOchagDamageValueForCauseForm5 = useCallback(() =>
    calculateTotalsByNasazhdeniyaForm5().dolapovr,
    [calculateTotalsByNasazhdeniyaForm5]
  );

  const focusData = useMemo(() => mobileSelectedFocusData(selectedForm45,
    selectedForm45 === 4 ? form4Data : form5Data, formData?.form6 || formData,
    handbooks, independentForm2Data, form1Data, dataType),
    [selectedForm45, form4Data, form5Data, formData, handbooks, independentForm2Data, form1Data, dataType]);


  // Обработчики для слайдера
  const handleTableScroll = useCallback(() => {
    if (tableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setTableScrollPosition(percentage);
    }
  }, []);

  const handleTableSliderChange = useCallback((e) => {
    const newPosition = parseInt(e.target.value);
    setTableScrollPosition(newPosition);
    if (tableScrollRef.current) {
      const maxScroll = tableScrollRef.current.scrollWidth - tableScrollRef.current.clientWidth;
      const scrollLeft = (newPosition / 100) * maxScroll;
      tableScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Обработчики для слайдера расчетной таблицы
  const handleCalculationsScroll = useCallback(() => {
    if (calculationsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = calculationsScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setCalculationsScrollPosition(percentage);
    }
  }, []);

  const handleCalculationsSliderChange = useCallback((e) => {
    const newPosition = parseInt(e.target.value);
    setCalculationsScrollPosition(newPosition);
    if (calculationsScrollRef.current) {
      const maxScroll = calculationsScrollRef.current.scrollWidth - calculationsScrollRef.current.clientWidth;
      const scrollLeft = (newPosition / 100) * maxScroll;
      calculationsScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Инициализация слайдеров
  useEffect(() => {
    const handleTableScroll = () => {
      if (tableScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setTableScrollPosition(percentage);
      }
    };

    const handleCalculationsScroll = () => {
      if (calculationsScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = calculationsScrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setCalculationsScrollPosition(percentage);
      }
    };

    // Устанавливаем обработчики событий
    const tableElement = tableScrollRef.current;
    const calculationsElement = calculationsScrollRef.current;

    if (tableElement) {
      tableElement.addEventListener('scroll', handleTableScroll);
    }

    if (calculationsElement) {
      calculationsElement.addEventListener('scroll', handleCalculationsScroll);
    }

    // Инициализируем позиции слайдеров
    handleTableScroll();
    handleCalculationsScroll();

    // Очистка обработчиков при размонтировании
    return () => {
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleTableScroll);
      }
      if (calculationsElement) {
        calculationsElement.removeEventListener('scroll', handleCalculationsScroll);
      }
    };
  }, []);

  return (
    <>
      <div className="template-section">
        <div 
          className="template-header" 
          style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h3>VI. Сводка по насаждению и рекомендуемые мероприятия</h3>
        </div>
        
        <div className="template-content">
          {/* Таблица Данные */}
          <DataSection
            displayData={displayData}
            updateCommonField={updateCommonField}
            independentForm2Data={independentForm2Data}
            calculateTotalsByNasazhdeniya={calculateTotalsByNasazhdeniya}
            tableScrollRef={tableScrollRef}
            tableScrollPosition={tableScrollPosition}
            handleTableSliderChange={handleTableSliderChange}
            handleTableScroll={handleTableScroll}
            hasForm4Data={selectedForm45 === 4 && form4Data && form4Data.data && form4Data.data.length > 0 && 
              form4Data.data.some(item => item.yarus || item.kodporoda || item.dolap)}
            form4SavedModalData={form4Data?.savedModalData || {}}
            form5Data={form5Data}
            selectedForm45={selectedForm45}
            handbooks={handbooks}
            landCategory={(form2Data?.[dataType] || form2Data?.to || form2Data?.actual || form2Data)?.katzem}
          />

          {/* Таблица расчетов */}
          <CalculationsSection
            displayData={displayData}
            calculateTotalsByNasazhdeniya={calculateTotalsByNasazhdeniya}
            calculationsScrollRef={calculationsScrollRef}
            calculationsScrollPosition={calculationsScrollPosition}
            handleCalculationsSliderChange={handleCalculationsSliderChange}
            handleCalculationsScroll={handleCalculationsScroll}
            hasForm4Data={selectedForm45 === 4 && form4Data && form4Data.data && form4Data.data.length > 0 && 
              form4Data.data.some(item => item.yarus || item.kodporoda || item.dolap)}
            form4Data={form4Data}
            form4SavedModalData={form4Data?.savedModalData || {}}
            form5Data={form5Data}
            handbooks={handbooks}
            independentForm2Data={independentForm2Data}
            form2Data={form2Data}
            dataType={dataType}
            selectedForm45={selectedForm45}
            form1Data={form1Data}
          />

          {/* Блок очагов: карточки для Form4 и Form5 */}
          {selectedForm45 === 4 ? (
            <OchagCardsSection
              form4Data={form4Data}
              form1Data={form1Data}
              dataType={dataType}
              form6Data={formData?.form6}
              handbooks={handbooks}
              independentForm2Data={independentForm2Data}
              formData={focusData}
              getOchagDamageValueForCause={getOchagDamageValueForCause}
            />
          ) : (
            <OchagCardsSectionForm5
              form5Data={form5Data}
              handbooks={handbooks}
              independentForm2Data={independentForm2Data}
              form1Data={form1Data}
              dataType={dataType}
              calculateTotalsByNasazhdeniya={calculateTotalsByNasazhdeniyaForm5}
              calculateDamagePercentage_func={calculateDamagePercentage_func_Form5}
              formData={focusData}
              onOchagClick={null}
              selectedOchags={new Set(focusData.selectedOchags || [])}
              isDisabled={true}
              showOnlySelected={true}
              calculateForm5TotalDamage={() => {
                const totals = calculateTotalsByNasazhdeniyaForm5 ? calculateTotalsByNasazhdeniyaForm5() : {};
                return totals?.dolapovr || '0.00';
              }}
              getOchagDamageValueForCause={getOchagDamageValueForCauseForm5}
            />
          )}

          {/* Наличие рекомендованных мероприятий */}
          <div
            className="group-section"
            style={{
              marginTop: '30px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              flexWrap: 'wrap'
            }}
          >
            {/* Слева — замечание, пока выбор не сделан */}
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              {hasRecommendedMeasures === null && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '6px 10px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '13px'
                  }}
                >
                  {`${RECOMMENDED_MEASURES_NOTICE} — мероприятия не заполнены`}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label
                htmlFor="form6-has-recommended-measures"
                style={{ margin: 0, fontWeight: 600, fontSize: '14px' }}
              >
                Наличие рекомендованных мероприятий
              </label>
              <select
                id="form6-has-recommended-measures"
                value={hasRecommendedMeasures === true ? 'yes' : hasRecommendedMeasures === false ? 'no' : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateCommonField(
                    'has_recommended_measures',
                    v === 'yes' ? true : v === 'no' ? false : null
                  );
                }}
                style={{
                  minWidth: '140px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  cursor: 'pointer'
                }}
              >
                <option value="">Не выбрано (нет)</option>
                <option value="no">Нет</option>
                <option value="yes">Да</option>
              </select>
            </div>
          </div>

          {/* Таблица рекомендуемых мероприятий — только при «Да» */}
          {hasRecommendedMeasures === true && (
            <RecommendedMeasuresSection
              recommendedMeasures={recommendedMeasures}
              handbooks={handbooks}
              onAddRow={handleAddRecommendedMeasure}
              onDeleteRow={handleDeleteRecommendedMeasure}
              onUpdateField={handleUpdateRecommendedMeasureField}
              form1Data={form1Data}
              dataType={dataType}
              selectedForm45={selectedForm45}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Form6;

